/* eslint-disable react/jsx-no-constructed-context-values */
import React, { createContext, useCallback, useEffect, useRef } from 'react';
import jwtDecode from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import userManager from './userManager';
import { signinRedirect as userManagerSigninRedirect } from './userManager';
import { signoutRedirect as userManagerSignoutRedirect } from './userManager';
import { initialState } from './slice/reducers';
import { actions as authActions } from './slice';
import { selectAuthState, selectCurrentUser } from './slice/selectors';
import { getAuthenticatedUser, impersonateUser } from './userProfileApi';
import Loader from '../components/Loader';
import useSettings from '../hooks/useSettings';
import LocaleService from '../services/localeService';
import { RouteNames } from '../constants/Routes';
import { formatDateTime } from '../utils/formatters';

const AuthContext = createContext({
	isAuthenticated: initialState.isAuthenticated,
	isInitialised: initialState.isInitialised,
	authenticatedUser: initialState.authenticatedUser,
	isImpersonating: initialState.isImpersonating,
	impersonatedUser: initialState.impersonatedUser,
	impersonate: () => Promise.resolve(),
	endImpersonation: () => {},
	getCurrentUser: () => {},
	signinRedirect: () => {},
	signoutRedirect: () => {},
});

export const AuthProvider = React.memo(({ children }) => {
	const dispatch = useDispatch();
	const state = useSelector(selectAuthState);
	const currentUser = useSelector(selectCurrentUser);
	const authInitialiseInFlightRef = useRef(null);
	const pathname = window?.location?.pathname || '';
	const isSilentRenewRoute = pathname.endsWith(RouteNames.SILENT_RENEW);
	const { saveSettings } = useSettings();
	const navigate = useNavigate();

	const signinRedirect = useCallback(
		(returnPath) => userManagerSigninRedirect(returnPath),
		[]
	);

	const signoutRedirect = useCallback(() => userManagerSignoutRedirect(), []);

	/** ----------------------------------------------------------------------------------------------------
	 * Trigger an impersonation request
	 * @param {*} email email address of user to impersonate
	 */ //---------------------------------------------------------------------------------------------------
	const impersonate = async (email) => {
		if (!email) {
			return;
		}

		try {
			const sessionUser = await userManager.getUser();
			const accessToken = state.accessToken || sessionUser?.access_token;
			const user = await impersonateUser(email, accessToken);

			if (!user) {
				throw new Error(
					'Impersonated user was not returned from authenticated request.'
				);
			}

			const impersonatedUser = { ...user, sub: state.authenticatedUser?.sub }; // copy the sub value from the logged in user
			dispatch(authActions.impersonate({ impersonatedUser }));
		} catch (err) {
			console.error(err);
		}
	};

	/** ----------------------------------------------------------------------------------------------------
	 * Ends active user impersonation and restores the authenticated user context
	 */ //---------------------------------------------------------------------------------------------------
	const endImpersonation = () => {
		dispatch(authActions.endImpersonation());
	};

	/** ----------------------------------------------------------------------------------------------------
	 * Returns the current authenticated user or impersonated user if impersonating
	 * @returns
	 */ //---------------------------------------------------------------------------------------------------
	const getCurrentUser = () => currentUser;

	/** ----------------------------------------------------------------------------------------------------
	 * Sets and persists locale from the OIDC profile when available
	 * @param {*} profile user profile returned by OIDC
	 */ //---------------------------------------------------------------------------------------------------
	const setLocale = useCallback(
		(profile) => {
			if (profile?.locale) {
				const locale = LocaleService.mapProfileToLocale(profile);
				saveSettings({ locale });
			}
		},
		[saveSettings]
	);

	// --------------------------------------------------
	// Initializes the authentication in redux store
	// --------------------------------------------------
	const updateAuthState = useCallback(
		(
			isAuthenticated,
			authenticatedUser = null,
			accessToken = null,
			idToken = null
		) => {
			dispatch(
				authActions.initialise({
					isAuthenticated,
					authenticatedUser,
					accessToken,
					idToken,
				})
			);
		},
		[dispatch]
	);

	// ------------------------------------------------------------------------------
	// Bootstraps auth state from the current OIDC session on first provider mount.
	// ------------------------------------------------------------------------------
	useEffect(() => {
		const initialise = async () => {
			if (isSilentRenewRoute) {
				updateAuthState(false);
				return;
			}

			if (state.isInitialised) {
				return;
			}

			if (authInitialiseInFlightRef.current) {
				await authInitialiseInFlightRef.current;
				return;
			}

			authInitialiseInFlightRef.current = (async () => {
				try {
					await userManager.clearStaleState();

					const user = await userManager.getUser();

					if (!user || user.expired) {
						updateAuthState(false);
						return;
					}

					await userManager.storeUser(user); // Kickstart internal timers - force timers to sync

					const accessToken = user.access_token;
					const idToken = user.id_token;
					const email = user.profile?.preferred_username;

					setLocale(user.profile);

					try {
						const userByEmail = await getAuthenticatedUser(email, accessToken);

						if (!userByEmail) {
							throw new Error(
								'User profile was not returned from authenticated bootstrap request.'
							);
						}

						const { sub } = jwtDecode(idToken);
						const cloneUser = { ...userByEmail, sub };

						updateAuthState(true, cloneUser, accessToken, idToken);
					} catch (err) {
						console.error('[OIDC] Profile fetch failed:', err);
						await userManager.clearStaleState();
						await userManager.removeUser();
						updateAuthState(false);
						navigate('/access-required', { replace: true });
					}
				} catch (err) {
					updateAuthState(false);
				}
			})().finally(() => {
				authInitialiseInFlightRef.current = null;
			});

			await authInitialiseInFlightRef.current;
		};

		initialise();
	}, [
		isSilentRenewRoute,
		navigate,
		setLocale,
		state.isInitialised,
		updateAuthState,
	]);

	// ------------------------------------------------------------------------------
	// Subscribes to token lifecycle events to keep redux auth tokens in sync.
	// ------------------------------------------------------------------------------
	useEffect(() => {
		if (isSilentRenewRoute) {
			return undefined;
		}

		// ------------------------------------------------------------------------------
		// Handles when user is loaded.  Updates session and redux with user info
		// ------------------------------------------------------------------------------
		const handleUserLoaded = async (user) => {
			const { expires_in: expiresIn, refresh_token: refreshToken } = user;
			const isAutoRenewOn = userManager.settings.automaticSilentRenew;
			const expirationDate = new Date(Date.now() + expiresIn * 1000);

			console.log(
				`[OIDC] Token renewed. \n` +
					`• Next Expiration: ${formatDateTime(
						expirationDate
					)} (${expiresIn} seconds) \n` +
					`• Refresh Token present: ${!!refreshToken} \n` +
					`• Automatic Silent Renew enabled: ${isAutoRenewOn}`
			);

			dispatch(authActions.setAccessToken(user.access_token));
			dispatch(authActions.setIdToken(user.id_token));
			setLocale(user.profile);

			// Refetch profile after renew so Redux has up-to-date user claims/permissions.
			const email = user?.profile?.preferred_username;
			if (!email || !user?.access_token) {
				return;
			}

			try {
				const refreshedProfile = await getAuthenticatedUser(
					email,
					user.access_token
				);

				if (!refreshedProfile) {
					return;
				}

				const { sub } = jwtDecode(user.id_token);
				dispatch(
					authActions.login({
						authenticatedUser: { ...refreshedProfile, sub },
					})
				);
			} catch (err) {
				console.warn('[OIDC] Token renewed, but profile refresh failed:', err);
			}
		};

		// ------------------------------------------------------------------------------
		// Handles when token expired.  Attempt to silently get a new token using the
		// refresh token but if that has expired too, then redirect to the sign in page.
		// ------------------------------------------------------------------------------
		const onAccessTokenExpired = async () => {
			console.log('[OIDC] Access token expired. Attempting silent renewal...');
			try {
				await userManager.signinSilent();
			} catch (error) {
				await userManager.clearStaleState();
				await userManager.signinRedirect();
			}
		};

		// -------------------------------------
		// Add event listeners to manage tokens
		// -------------------------------------
		userManager.events.addUserLoaded(handleUserLoaded);
		userManager.events.addAccessTokenExpired(onAccessTokenExpired);

		return () => {
			userManager.events.removeUserLoaded(handleUserLoaded);
			userManager.events.removeAccessTokenExpired(onAccessTokenExpired);
		};
	}, [dispatch, isSilentRenewRoute, setLocale]);

	// -------------------------------------------------------
	// Provide some troubleshooting logging regarding tokens
	// -------------------------------------------------------
	useEffect(() => {
		if (isSilentRenewRoute) {
			return undefined;
		}

		const onExpiring = () =>
			console.log('[OIDC] ID Token expiring in 3 minutes. Triggering renew...');
		const onRenewError = (err) =>
			console.error('[OIDC] Automatic ID token renewal failed:', err);

		userManager.events.addAccessTokenExpiring(onExpiring);
		userManager.events.addSilentRenewError(onRenewError);

		return () => {
			userManager.events.removeAccessTokenExpiring(onExpiring);
			userManager.events.removeSilentRenewError(onRenewError);
		};
	}, [isSilentRenewRoute]);

	if (!state.isInitialised) {
		return <Loader />;
	}

	const contextState = {
		isAuthenticated: state.isAuthenticated,
		isInitialised: state.isInitialised,
		authenticatedUser: state.authenticatedUser,
		isImpersonating: state.isImpersonating,
		impersonatedUser: state.impersonatedUser,
	};

	return (
		<AuthContext.Provider
			value={{
				...contextState,
				impersonate,
				endImpersonation,
				getCurrentUser,
				signinRedirect,
				signoutRedirect,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
});

AuthProvider.propTypes = {
	children: PropTypes.any.isRequired,
};

export default AuthContext;
